//react
import React from 'react';
import { connect } from 'react-redux';

//packages
import { ResponsivePie } from '@nivo/pie';
import Moment from 'moment';
import { get as _get, startCase } from 'lodash';

//utils
import { updateCampaignAggregates } from '../../store/api/calls';
import { pullCampaignDoorCanvassPasses } from '../../store/api/calls/doors';
import { setDashboardParams } from '../../store/actions';
import api from '../../store/api/api';

//components
import NavBaseTemplate from '../templates/NavBaseTemplate';
import FromToDatePickerInput from '../theme/forms/FromToDatePickerInput';
import FeedbackTile from '../theme/modals/FeedbackTile';
import DashboardOfficeSelector from '../theme/dashboard/selectors/DashboardOfficeSelector';
import DoorCanvassPassSelector from '../theme/dashboard/selectors/DoorCanvassPassSelector';
import DoorCanvassPassSelectionModal from '../theme/dashboard/selectors/DoorCanvassPassSelectionModal';
import DashTabs from '../theme/menus/DashTabs';

class DoorCanvassStatusReport extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      error: null,
      data: [],
      displayData: [],
    };
    this.SUBMIT_DATE_FORMAT = 'YYYY-MM-DD';
    this.ERROR_TIMEOUT = null;
    this.ERROR_TIMEOUT_DURATION = 3000;
  }

  handleDateChange = (start, end) => {
    this.props.setDashboardParams({
      start: start.getTime(),
      end: end.getTime(),
    });
  };

  componentDidMount() {
    this.setState({ loading: true });
    this.props.setDashboardParams({ selectedCanvasser: 0 });

    let promises = [pullCampaignDoorCanvassPasses(), this._updateCampaignAggregates()];

    Promise.allSettled(promises).then(() => {
      console.log(this.props.doorCanvassPasses);
      this.setState({ loading: false });
      if (!this.props.doorCanvassPasses.length) {
        this.setState({ displayDCPSelectorModal: false });
      } else {
        if (
          this.props.dashboardParams.selectedDoorCanvassPass &&
          this.props.doorCanvassPasses
            .map((d) => d.id)
            .includes(this.props.dashboardParams.selectedDoorCanvassPass)
        ) {
          this._getData();
        } else {
          this.setState({ displayDCPSelectorModal: true });
        }
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.focusedCampaign.id !== this.props.focusedCampaign.id) {
      this.setState({ loading: true });
      pullCampaignDoorCanvassPasses().finally(() => {
        this.props.setDashboardParams({
          selectedOffice: 0,
          selectedDoorCanvassPass: 0,
        });
        this.setState({ loading: false });
        //display modal only if pass not set
        this.setState({
          displayDCPSelectorModal: !!this.props.doorCanvassPasses.length,
        });
      });
    }
    if (
      prevProps.dashboardParams.start !== this.props.dashboardParams.start ||
      prevProps.dashboardParams.end !== this.props.dashboardParams.end ||
      prevProps.dashboardParams.selectedDoorCanvassPass !==
        this.props.dashboardParams.selectedDoorCanvassPass
    ) {
      this._getData();
      this._updateCampaignAggregates();
    }

    if (prevProps.dashboardParams.selectedOffice !== this.props.dashboardParams.selectedOffice) {
      this.setState({
        displayData: this.parseDataForDisplay(),
      });
    }
  }

  _updateCampaignAggregates = () => {
    return updateCampaignAggregates(
      Moment(this.props.dashboardParams.start).format(this.SUBMIT_DATE_FORMAT),
      Moment(this.props.dashboardParams.end).format(this.SUBMIT_DATE_FORMAT)
    );
  };

  _getFocusedDoorCanvassPass = () =>
    this.props.doorCanvassPasses.find(
      (dcp) => dcp.id === this.props.dashboardParams.selectedDoorCanvassPass
    ) || {};

  _getData = (dcp_id) => {
    if (!dcp_id) dcp_id = this.props.dashboardParams.selectedDoorCanvassPass;

    if (!dcp_id) return new Promise((resolve) => resolve());

    api
      .get('door-status-counts/', {
        start: new Moment(this.props.dashboardParams.start).format(this.SUBMIT_DATE_FORMAT),
        end: new Moment(this.props.dashboardParams.end).format(this.SUBMIT_DATE_FORMAT),
        doorcanvasspass: dcp_id,
      })
      .then((res) => {
        this.setState({
          data: res.data || [],
          displayData: this.parseDataForDisplay(res.data),
          error: null,
        });
      })
      .catch((err) => {
        if (!err.response.status) {
          this._setError('Unable to connect');
        } else {
          this._setError('Server error.');
        }
      });
  };

  _setError = (errMsg) => {
    clearTimeout(this.ERROR_TIMEOUT);
    this.setState({ error: errMsg }, () => {
      this.ERROR_TIMEOUT = setTimeout(() => {
        this.setState({ error: null }, this.ERROR_TIMEOUT_DURATION);
      });
    });
  };

  parseDataForDisplay = (sourceData = this.state.data) => {
    let displayData = [];
    if (!sourceData.length) return displayData;

    if (this.props.dashboardParams.selectedOffice) {
      sourceData = sourceData.filter(
        (d) => d.office_id === this.props.dashboardParams.selectedOffice
      );
    }

    displayData = sourceData.map((d) => {
      let total_count = (d.statuses || []).reduce((r, s) => r + s.count, 0);
      return {
        ...d,
        name: `${startCase(d.firstname)} ${startCase(d.lastname)} (${total_count})`,
        total_count,
        chartData: d.statuses.map((s) => ({
          id: s.status,
          value: s.count,
          label: startCase(s.status),
          color: s.status_color,
        })),
      };
    });

    //we are not going to make a total pie, to be placed in the zero category.

    let selectedDCP = this._getFocusedDoorCanvassPass();
    let totals_data = {
      name: 'Totals for Pass',
      total_count: 0,
    };

    totals_data.chartData = displayData.reduce((r, d) => {
      let { statuses } = d;

      statuses.forEach((s) => {
        let count = s.count || 0;
        let matching_obj = r.find((_d) => _d.id === s.status);
        if (matching_obj) {
          matching_obj['value'] += count;
        } else {
          r.push({
            id: s.status,
            value: count,
            label: startCase(s.status),
            color: s.status_color,
          });
        }

        totals_data.total_count += count;
      });

      return r;
    }, []);

    //add percentage to the chart data labels
    totals_data.chartData.forEach((d) => {
      d.label = `${d.label} (${Math.round((d.value / totals_data.total_count) * 100)}%)`;
    });

    if (totals_data.total_count) {
      totals_data.name = `${totals_data.name} (${totals_data.total_count})`;
    }

    return [totals_data, ...displayData];
  };

  renderChartGrid = () => {
    let displayData = [...this.state.displayData];

    // if (this.props.dashboardParams.selectedOffice) {
    //     displayData = displayData.filter(d => d.office_id === this.props.dashboardParams.selectedOffice)
    // }

    let totalsObject = displayData.shift();

    if (!totalsObject)
      totalsObject = {
        name: 'No data during this period.',
        chartData: [
          {
            id: 'nada',
            label: 'Nada',
            color: '#dfdfdf',
            value: 1,
          },
        ],
      };

    //chart props stuff
    const defs = [
      {
        id: 'dots',
        type: 'patternDots',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.3)',
        size: 4,
        padding: 1,
        stagger: true,
      },
      {
        id: 'lines',
        type: 'patternLines',
        background: 'inherit',
        color: 'rgba(255, 255, 255, 0.3)',
        rotation: -45,
        lineWidth: 6,
        spacing: 10,
      },
    ];

    const fillProp = [
      {
        match: {
          id: 'moved',
        },
        id: 'lines',
      },
      {
        match: {
          id: 'inaccessible',
        },
        id: 'lines',
      },
      {
        match: {
          id: 'do_not_walk',
        },
        id: 'lines',
      },
      {
        match: {
          id: 'refused',
        },
        id: 'lines',
      },
    ];
    // DOCUMENTATION: https://nivo.rocks/pie/
    return (
      <div className={'chart-grid'}>
        <div className={'top-chart full-width'}>
          <h5 className={'primary-milligram-text'}>{totalsObject.name}</h5>
          <ResponsivePie
            data={totalsObject.chartData}
            colors={(datum) => datum.data.color}
            margin={{ top: 30, right: 10, bottom: 30, left: 10 }}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            borderWidth={3}
            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
            arcLinkLabel={(d) => d.label}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={10}
            motionConfig="stiff"
            // arcLabelsTextColor={{ from: 'color', modifiers: [ [ 'darker', 2 ] ] }}
            defs={defs}
            fill={fillProp}
          />
        </div>
        <div className={'row'} style={{ flexWrap: 'wrap' }}>
          {displayData.map((d) => {
            return (
              <div className={'column column-25'}>
                <div className={'row-chart'}>
                  <p className={'primary-milligram-text'}>{d.name}</p>
                  <ResponsivePie
                    data={d.chartData}
                    colors={(datum) => datum.data.color}
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                    innerRadius={0.4}
                    padAngle={0.7}
                    cornerRadius={2}
                    activeOuterRadiusOffset={4}
                    borderWidth={1}
                    borderColor={{
                      from: 'color',
                      modifiers: [['darker', 0.2]],
                    }}
                    enableArcLinkLabels={false}
                    // motionConfig="stiff"
                    animate={false}
                    isInteractive={true}
                    // arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{
                      from: 'color',
                      modifiers: [['darker', 2]],
                    }}
                    defs={defs}
                    fill={fillProp}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    return (
      <NavBaseTemplate location={this.props.location} showAdmin={this.props.is_admin}>
        <DashTabs />
        <h3 className={'primary-text'}>Door Canvass Statuses</h3>
        <div className="button-table-top-wrapper">
          <FromToDatePickerInput
            handleDateChange={this.handleDateChange}
            from={new Date(this.props.dashboardParams.start)}
            to={new Date(this.props.dashboardParams.end)}
          />
          <DashboardOfficeSelector />
          <DoorCanvassPassSelector />
        </div>

        {!this.props.doorCanvassPasses.length && (
          <p>There are no door canvasses in this campaign.</p>
        )}

        {this.renderChartGrid()}

        <DoorCanvassPassSelectionModal
          open={this.state.displayDCPSelectorModal}
          afterSelect={(selectedPassId) => this.setState({ displayDCPSelectorModal: false })}
        />

        <FeedbackTile
          text={
            <div>
              <i className=" fas fa-asterisk fa-spin" style={{ marginRight: '15px' }} />
              Loading...
            </div>
          }
          visible={this.state.loading}
        />

        {/*use feedback tile icon, text, state*/}
        <FeedbackTile
          text={
            <div>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '15px' }} />
              {this.state.error}
            </div>
          }
          visible={this.state.error}
          backgroundColor={'red'}
        />
      </NavBaseTemplate>
    );
  }
}

const mapStateToProps = (state) => ({
  campaigns: state.session.campaigns,
  focusedCampaign: state.session.focusedCampaign,
  is_admin: state.permissions.is_admin,
  dashboardParams: state.dashboardParams,
  doorCanvassPasses: state.doorCanvassPasses.data,
});

const mapDispatchToProps = (dispatch) => ({
  setDashboardParams: (data) => dispatch(setDashboardParams(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DoorCanvassStatusReport);
